<template>
  <b-container>
    <b-row>
      <b-col>
        <h2>Register</h2>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form>
          <b-form-row>
            <b-col sm="12" md="6">
              <b-form-group id="fieldset-firstName" label="Firstname" label-for="firstName" label-size="sm">
                <b-form-input
                  v-model="firstName"
                  type="text"
                  id="firstName"
                  placeholder="Enter firstname"
                  size="sm"
                  trim
                  :required="true"
                />
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6">
              <b-form-group id="fieldset-lastName" label="Lastname" label-for="lastName" label-size="sm">
                <b-form-input
                  id="lastName"
                  type="text"
                  placeholder="Enter lastname"
                  v-model="lastName"
                  size="sm"
                  trim
                  :required="true"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col sm="12" md="6">
              <b-form-group id="fieldset-email" label="Email address" label-for="email" label-size="sm">
                <b-form-input
                  type="email"
                  v-model="email"
                  placeholder="Enter email"
                  id="email"
                  size="sm"
                  trim
                  :required="true"
                />
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6">
              <b-form-group id="fieldset-password" label="Password" label-for="password" label-size="sm">
                <b-input-group>
                  <b-form-input
                    v-model="password"
                    type="text"
                    id="password"
                    placeholder="Password"
                    size="sm"
                    trim
                    :required="true"
                  />
                  <b-input-group-append>
                    <b-btn size="sm" variant="info" @click="generatePassword">Generate</b-btn>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col sm="12" md="6">
              <b-form-group
                id="fieldset-supplier"
                label="Supplier"
                label-for="supplier"
                label-size="sm"
                description="This field can be use to search for an existing supplier (optional)"
              >
                <autocomplete
                  is-async
                  id="supplier"
                  :items="getCreditors()"
                  @input="debounceSuppliers"
                  @select="setCreditorId"
                  v-model="supplierSearch"
                ></autocomplete>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6">
              <b-form-group
                id="fieldset-comment"
                label="Comment"
                label-for="comment"
                label-size="sm"
                description="This is an optional field for remarks about the supplier"
              >
                <b-form-textarea id="comment" size="sm" placeholder="Enter comment" v-model="comment" rows="3">
                </b-form-textarea>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-row align-h="between">
            <b-col>
              <b-btn @click="resetForm()" variant="outline-danger">
                Reset
              </b-btn>
            </b-col>
            <b-col class="text-right">
              <b-btn @click="handleRegistration()" variant="primary" :disabled="!isValidForm">
                Submit
              </b-btn>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>
    <b-row>
      <div class="col md-12 mt-3 alert alert-danger" v-if="registrationErrors">
        {{ registrationErrors.title }}
        <ul id="registration-errors">
          <li v-bind:key="error" v-for="error in registrationErrors.errors">
            {{ error }}
          </li>
        </ul>
      </div>
      <div class="col md-12 mt-3 alert alert-success" v-if="registrationSuccess">
        <p>
          Registration successful. You may now login to the supplier portal.
        </p>
        <router-link :to="{ name: 'login' }" class="btn btn-small btn-success">Login </router-link>
      </div>
    </b-row>
  </b-container>
</template>

<script>
import store from 'src/store/index';
import { mapActions, mapState } from 'vuex';
import { GET_SUPPLIERS_ACTION } from '@/store/actionTypes';
import autocomplete from 'src/components/common/AutocompleteInput';
import _ from 'lodash';
import { SET_SELECTED_SUPPLIER } from '../store/mutationTypes';

export default {
  name: 'Register',
  components: {
    autocomplete,
  },
  data: function() {
    return {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      comment: '',
      creditorId: null,
      registrationErrors: '',
      registrationSuccess: false,
      supplierSearch: null,
    };
  },
  watch: {
    supplierSearch: function(search) {
      this.debounceSuppliers(search);
    },
  },
  computed: {
    isValidForm() {
      return this.validateForm();
    },
  },
  methods: {
    ...mapState({
      selectedSupplier: (state) => state.suppliers.selectedSupplier,
      getCreditors(state) {
        return state.suppliers.all.map((creditor) => ({
          key: creditor.id,
          value: creditor.name,
        }));
      },
    }),
    ...mapActions({
      getSuppliers: GET_SUPPLIERS_ACTION,
      setSupplier: SET_SELECTED_SUPPLIER,
    }),
    setCreditorId(event) {
      this.creditorId = event.key;
    },
    debounceSuppliers: _.debounce(async function() {
      this.getSuppliers(this.supplierSearch);
    }, 1000),
    handleRegistration() {
      this.registrationErrors = null;
      this.registrationSuccess = false;

      store
        .dispatch('register', {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          comment: this.comment,
          password: this.password,
          creditorId: this.creditorId,
        })
        .then(() => {
          this.registrationSuccess = true;
        })
        .catch((error) => {
          this.registrationErrors = error.response.data;
        });
    },
    validateForm() {
      return this.validateEmail() && this.firstName !== '' && this.lastName !== '' && this.password !== '';
    },
    validateEmail() {
      /* eslint no-control-regex: "off" */
      return /^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*$/.test(
        this.email,
      );
    },
    resetForm() {
      this.email = '';
      this.password = '';
      this.firstName = '';
      this.lastName = '';
      this.comment = '';
      this.creditorId = null;
    },
    generatePassword(e) {
      e.preventDefault();
      const length = 12,
        charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let retVal = '';
      for (let i = 0, n = charset.length; i < length; ++i) {
        retVal += charset.charAt(Math.floor(Math.random() * n));
      }
      this.password = retVal;
    },
  },
};
</script>

<style scoped></style>
